import * as React from 'react';
import {cn} from '../lib/cn';
import {Label} from '../label';
import {BaseCheckbox, type CheckboxSize} from './BaseCheckbox';
import {FieldErrorMessage} from '../field-error-message';

const rowSizeClasses: Record<CheckboxSize, string> = {
  default: 'gap-2.5',
  m: 'gap-2.5',
  s: 'gap-2',
};

const labelSizeClasses: Record<CheckboxSize, string> = {
  default: 'text-base',
  m: 'text-base',
  s: 'text-sm',
};

export interface CheckboxProps {
  id?: string;
  label?: React.ReactNode;
  children?: React.ReactNode;
  value?: boolean;
  onChange?: (checked: boolean) => void;
  onWrapperClick?: (checked: boolean) => void;
  disabled?: boolean;
  readOnly?: boolean;
  loading?: boolean;
  error?: string;
  supportingText?: string;
  className?: string;
  size?: CheckboxSize;
  rowClassName?: string;
  labelClassName?: string;
  checkboxClassName?: string;
  errorClassName?: string;
  supportingTextClassName?: string;
}

export const Checkbox = React.forwardRef<HTMLButtonElement, CheckboxProps>(
  (
    {
      id,
      label,
      value = false,
      children,
      onChange,
      onWrapperClick,
      error,
      supportingText,
      className,
      size = 'default',
      rowClassName,
      labelClassName,
      checkboxClassName,
      errorClassName,
      supportingTextClassName,
      ...props
    },
    ref,
  ) => {
    const generatedId = React.useId();
    const checkboxId = id || generatedId;
    const errorId = error ? `${checkboxId}-error` : undefined;
    const descriptionId = supportingText ? `${checkboxId}-desc` : undefined;
    const ariaDescribedBy = errorId || descriptionId || undefined;

    return (
      <div className={cn('checkbox flex flex-col', className)}>
        <div
          className={cn(
            'checkbox__row flex items-center',
            rowSizeClasses[size],
            rowClassName,
          )}
          onClick={() => onWrapperClick?.(!value)}
        >
          <BaseCheckbox
            id={checkboxId}
            ref={ref}
            checked={onChange !== undefined ? value : undefined}
            defaultChecked={onChange === undefined ? value : undefined}
            onCheckedChange={onChange ? checked => onChange(checked === true) : undefined}
            className={checkboxClassName}
            size={size}
            aria-invalid={!!error || undefined}
            aria-describedby={ariaDescribedBy}
            {...props}
          />
          {label && (
            <Label
              htmlFor={checkboxId}
              className={cn(
                'checkbox__label cursor-pointer font-medium leading-none text-[var(--checkbox-label-color)] peer-disabled:cursor-not-allowed peer-disabled:opacity-70',
                labelSizeClasses[size],
                labelClassName,
              )}
            >
              {label}
            </Label>
          )}
          {children}
        </div>
        {supportingText && !error && (
          <p
            id={descriptionId}
            className={cn(
              'checkbox__supporting-text mt-1 text-xs italic text-[var(--chekin-color-gray-2)]',
              supportingTextClassName,
            )}
          >
            {supportingText}
          </p>
        )}
        {error && (
          <FieldErrorMessage
            id={errorId}
            className={cn('checkbox__error', errorClassName)}
          >
            {error}
          </FieldErrorMessage>
        )}
      </div>
    );
  },
);

Checkbox.displayName = 'Checkbox';
